/**
 * @file        src/pages/platform/TenantBrandingPage.jsx
 * @description Tenant Management → Admin Portal Branding. The selected
 *              tenant's ADMIN-CONSOLE logo + favicon (login screen, sidebar,
 *              browser tab), written into the tenant DB's site_settings row.
 *              Moved out of the tenant edit page's stacked panels into its own
 *              sidebar entry (?tenant=<id> picks the tenant).
 *
 *              super_admin ONLY, and since 2026-08-10 exclusively so: the
 *              tenant admin's Site Setting page no longer carries these two
 *              fields (the backend strips them from PUT /site-settings), so
 *              this page is the one place the admin portal's look is set.
 *              The BOOKING portal's logo/favicon are per-brand, under Tenant
 *              Management → Brands (and editable later by the tenant admin).
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (extracted from AddTenant.jsx; now the sole
 *                          write path for admin-portal branding).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import ImageUpload from '../../components/ImageUpload';
import { get, put } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LOGO_CHECK, LOGO_HINT, FAVICON_CHECK, FAVICON_HINT } from '../../lib/brandingSpecs';
import TenantPicker, { useTenantParam } from './TenantPicker';
import Field from './Field';

export default function TenantBrandingPage() {
  const [tenantId, setTenantId] = useTenantParam();
  return (
    <>
      <PageHead title="Admin Portal Branding" subtitle="Platform · Tenant Management · console logo & favicon" />
      <article className="panel glass" style={{ marginBottom: 16 }}>
        <TenantPicker value={tenantId} onChange={setTenantId} />
      </article>
      {tenantId ? <TenantBranding tenantId={tenantId} /> : (
        <article className="panel glass"><div className="empty">Select a tenant to set its admin portal branding.</div></article>
      )}
    </>
  );
}

function TenantBranding({ tenantId }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ logo_url: '', favicon_url: '' });

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.tenants.branding(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/branding`).then((r) => r.data),
  });

  useEffect(() => {
    if (data) setForm({ logo_url: data.logo_url || '', favicon_url: data.favicon_url || '' });
  }, [data]);

  const mut = useMutation({
    mutationFn: (body) => put(`/tenants/${tenantId}/branding`, body),
    onSuccess: () => {
      toast.ok('Branding saved', "This tenant's admin portal logo and favicon are set.");
      qc.invalidateQueries({ queryKey: qk.tenants.branding(tenantId) });
      qc.invalidateQueries({ queryKey: qk.tenants.readiness(tenantId) });
    },
    onError: (e) => toast.err('Could not save', e.response?.data?.error?.message || 'Failed'),
  });

  return (
    <article className="panel glass">
      <div className="panel-head">
        <div>
          <h2>Admin Portal Branding</h2>
          <p className="muted">
            Logo and favicon for this tenant's admin console — login screen, sidebar and browser
            tab. Without them the portal shows the platform's default logo. Only a super admin can
            change these; the customer booking site's logo/favicon are set per brand under
            Tenant Management → Brands.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="empty">Loading branding…</div>
      ) : isError ? (
        <div className="empty"><p className="field-error">Could not load this tenant's branding.</p></div>
      ) : (
        <form
          className="form-grid"
          onSubmit={(e) => { e.preventDefault(); mut.mutate(form); }}
        >
          <Field label="Admin Portal Logo" hint={LOGO_HINT}>
            <ImageUpload value={form.logo_url} check={LOGO_CHECK}
                         onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} />
          </Field>
          <Field label="Favicon" hint={FAVICON_HINT}>
            <ImageUpload value={form.favicon_url} check={FAVICON_CHECK}
                         onChange={(v) => setForm((f) => ({ ...f, favicon_url: v }))} />
          </Field>
          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="check" /> {mut.isPending ? 'Saving…' : 'Save Branding'}
            </button>
          </div>
        </form>
      )}
    </article>
  );
}
